import type { inferRouterOutputs } from '@trpc/server';
import type { AppRouter } from '@wineryxplorer/api';
import type { DistanceLocale } from '@wineryxplorer/shared';
import { CompactVenueCard } from './CompactVenueCard';
import { EmptyState } from './EmptyState';
import { Pagination } from './Pagination';

export type VenueSearchResult = inferRouterOutputs<AppRouter>['venue']['search'];

interface ResultsListProps {
  isPending: boolean;
  errorMessage: string | undefined;
  data: VenueSearchResult | undefined;
  searchText: string;
  highlightedVenueId: string | null;
  onHighlightVenue: (venueId: string | null) => void;
  userLocation: { lat: number; lng: number } | null;
  distanceLocale: DistanceLocale;
  scopePillLabel: string | undefined;
  hasActiveFilters: boolean;
  isMapAreaMode: boolean;
  onClearFilters: () => void;
  onClearMapArea: () => void;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

/**
 * The results column of the Wineries search page: loading skeletons, error
 * state, the venue cards, the "found elsewhere" fallback, the empty state, and
 * pagination.
 */
export const ResultsList = ({
  isPending,
  errorMessage,
  data,
  searchText,
  highlightedVenueId,
  onHighlightVenue,
  userLocation,
  distanceLocale,
  scopePillLabel,
  hasActiveFilters,
  isMapAreaMode,
  onClearFilters,
  onClearMapArea,
  currentPage,
  totalPages,
  onPageChange,
}: ResultsListProps) => (
  <>
    {isPending ? (
      <div className="space-y-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex gap-4 p-3 rounded-lg border border-gray-200 animate-pulse">
            <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-lg bg-gray-200" />
            <div className="flex-1 space-y-2 py-1">
              <div className="h-4 bg-gray-200 rounded w-3/4" />
              <div className="h-3 bg-gray-200 rounded w-1/2" />
              <div className="h-3 bg-gray-200 rounded w-1/3" />
            </div>
          </div>
        ))}
      </div>
    ) : errorMessage ? (
      <div className="text-center py-12">
        <p className="text-red-600">Error loading venues: {errorMessage}</p>
      </div>
    ) : data && data.items.length > 0 ? (
      <div className="space-y-3">
        {data.items.map((venue) => (
          <div
            key={venue._id}
            className={`rounded-lg transition-shadow ${
              highlightedVenueId === venue._id ? 'shadow-lg' : ''
            }`}
            onMouseEnter={() => onHighlightVenue(venue._id)}
            onMouseLeave={() => onHighlightVenue(null)}
          >
            <CompactVenueCard
              venue={venue}
              userLocation={userLocation}
              distanceLocale={distanceLocale}
            />
          </div>
        ))}
      </div>
    ) : searchText &&
      data &&
      data.items.length === 0 &&
      data.elsewhereItems &&
      data.elsewhereItems.length > 0 ? (
      (() => {
        const scopeNoun = scopePillLabel
          ? scopePillLabel
              .replace(/^Searching (in|near) /, '')
              .replace(/^you$/, 'your area')
          : 'this area';
        const elsewhereCount = data.elsewhereTotal ?? data.elsewhereItems.length;
        return (
          <div role="status" aria-live="polite">
            <p className="text-sm text-gray-600 mb-2">
              No matches in {scopeNoun} for &ldquo;{searchText}&rdquo;.
            </p>
            <h3 className="text-base font-serif text-gray-900 mb-3 pt-4 border-t border-gray-200">
              Found elsewhere{elsewhereCount > data.elsewhereItems.length ? ` (${elsewhereCount})` : ''}
            </h3>
            <div className="space-y-3">
              {data.elsewhereItems.map((venue) => (
                <div key={venue._id} className="rounded-lg">
                  <CompactVenueCard
                    venue={venue}
                    userLocation={userLocation}
                    distanceLocale={distanceLocale}
                  />
                </div>
              ))}
            </div>
          </div>
        );
      })()
    ) : (
      <EmptyState
        hasActiveFilters={hasActiveFilters}
        hasDivergedViewport={isMapAreaMode}
        onClearFilters={onClearFilters}
        onZoomOut={onClearMapArea}
        fallbackItems={data?.fallbackItems ?? []}
        userLocation={userLocation}
        distanceLocale={distanceLocale}
      />
    )}

    {/* Pagination */}
    {data && totalPages > 1 && (
      <div className="mt-4">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={onPageChange}
        />
      </div>
    )}
  </>
);
